import { GamePack, Prompt } from '@/API';

type PromptWithGamePacks = Omit<Prompt, 'gamePacks'> & {
  associatedGamePacks?: GamePack[];
};
import { listGamePacks, listPrompts, updateGamePackOrder } from '@/client/gamePacks';
import LoadingDots from '@/components/Animated/Icons/LoadingDots';
import { CreateGamePackDialog } from '@/components/GamePacks/CreateGamePackDialog';
import { DeleteGamePackDialog } from '@/components/GamePacks/DeleteGamePackDialog';
import { EditGamePackDialog } from '@/components/GamePacks/EditGamePackDialog';
import { GamePackCard } from '@/components/GamePacks/GamePackCard';
import { CreatePromptDialog } from '@/components/GamePacks/Prompts/CreatePromptDialog';
import { DeletePromptDialog } from '@/components/GamePacks/Prompts/DeletePromptDialog';
import { EditPromptDialog } from '@/components/GamePacks/Prompts/EditPromptDialog';
import { PromptCard } from '@/components/GamePacks/Prompts/PromptCard';
import { Panel } from '@/components/Layouts/ContentPanel';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useEffect, useState } from 'react';

export default function GamePacksPage() {
  // Game Pack Dialogs
  const [isCreateGamePackDialogOpen, setIsCreateGamePackDialogOpen] = useState<boolean>(false);
  const [isEditGamePackDialogOpen, setIsEditGamePackDialogOpen] = useState<boolean>(false);
  const [isDeleteGamePackDialogOpen, setIsDeleteGamePackDialogOpen] = useState<boolean>(false);

  // Prompt Dialogs
  const [isCreatePromptDialogOpen, setIsCreatePromptDialogOpen] = useState<boolean>(false);
  const [isEditPromptDialogOpen, setIsEditPromptDialogOpen] = useState<boolean>(false);
  const [isDeletePromptDialogOpen, setIsDeletePromptDialogOpen] = useState<boolean>(false);
  const [gamePacks, setGamePacks] = useState<GamePack[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [activePrompt, setActivePrompt] = useState<Prompt | undefined>(undefined);
  const [activeGamePack, setActiveGamePack] = useState<GamePack | undefined>(undefined);
  const [promptsLoading, setPromptsLoading] = useState<boolean>(false);
  const [activeGamePackPrompts, setActiveGamePackPrompts] = useState<Prompt[]>([]);

  // For PL Admins, we want _all the prompts_
  const getAllPrompts = async () => {
    setPromptsLoading(true);

    try {
      // Get all prompts with their gamepack associations
      const allPrompts = await listPrompts();
      setPrompts(allPrompts as Prompt[]);
    } catch (error) {
      console.error('Error in getAllPrompts:', error);
      const fallbackPrompts = await listPrompts();
      setPrompts(fallbackPrompts as Prompt[]);
    }

    setPromptsLoading(false);
  };

  // Get prompts with their associated GamePacks for display
  const getPromptsWithGamePacks = (): PromptWithGamePacks[] => {
    if (activeGamePack?.id) {
      // If a GamePack is selected, show only its prompts but preserve their actual gamepack associations
      return activeGamePackPrompts.map((prompt) => ({
        ...prompt,
        associatedGamePacks: (prompt as any).gamePacks || []
      })) as PromptWithGamePacks[];
    } else {
      // If no GamePack is selected, show all prompts with their actual gamepack associations
      return prompts.map((prompt) => ({
        ...prompt,
        associatedGamePacks: (prompt as any).gamePacks || []
      })) as PromptWithGamePacks[];
    }
  };

  const getGamePacks = () => {
    return listGamePacks().then((items) => {
      setGamePacks((items as unknown as GamePack[]) ?? []);
    });
  };

  const getGamePackPrompts = async () => {
    if (activeGamePack?.id) {
      // Instead of getting prompts from the gamepack (which might not have full associations),
      // get all prompts and filter to only show those that belong to the active gamepack
      try {
        const allPrompts = await listPrompts();
        const gamePackPrompts = allPrompts.filter((prompt) =>
          (prompt.gamePacks ?? []).some((gamePack) => gamePack.id === activeGamePack.id)
        );
        setActiveGamePackPrompts((gamePackPrompts as unknown as Prompt[]) || []);
      } catch (error) {
        console.error('Error fetching game pack prompts:', error);
      }
    }
  };

  const refreshGamePackData = async () => {
    await Promise.all([getGamePacks(), getAllPrompts()]);
    if (activeGamePack?.id) {
      await getGamePackPrompts();
      // Also refresh the activeGamePack state
      const freshGamePacks = await listGamePacks();
      const updatedGamePack = freshGamePacks.find((gp) => gp.id === activeGamePack.id);
      if (updatedGamePack) {
        setActiveGamePack(updatedGamePack as unknown as GamePack);
      }
    }
  };

  useEffect(() => {
    getGamePacks();
    getAllPrompts();
  }, []);

  useEffect(() => {
    getGamePackPrompts();
  }, [activeGamePack]);

  const gamePackEdit = (gamePack: GamePack) => {
    setActiveGamePack(gamePack);
    if (!gamePack.id) {
      return;
    }
    setIsEditGamePackDialogOpen(true);
  };

  const gamePackDelete = (gamePack: GamePack) => {
    setActiveGamePack(gamePack);
    if (!gamePack.id) {
      return;
    }
    setIsDeleteGamePackDialogOpen(true);
  };

  const promptEdit = (prompt: Prompt) => {
    setActivePrompt(prompt);
    setIsEditPromptDialogOpen(true);
  };

  const promptDelete = (prompt: Prompt) => {
    setActivePrompt(prompt);
    setIsDeletePromptDialogOpen(true);
  };

  const toggleGamePack = (gamePack: GamePack) => {
    if (activeGamePack?.id && activeGamePack?.id === gamePack.id) {
      setActiveGamePack(undefined);
    } else {
      setActiveGamePackPrompts([]);
      setActiveGamePack(gamePack);
    }
  };

  const toggleEdit = (isOpen: boolean) => {
    if (!isOpen) {
      setActivePrompt(undefined);
    }
    setIsEditPromptDialogOpen(isOpen);
  };

  const toggleDelete = (isOpen: boolean) => {
    if (!isOpen) {
      setActivePrompt(undefined);
    }
    setIsDeletePromptDialogOpen(isOpen);
  };

  const toggleGamePackCreate = (isOpen: boolean) => {
    if (!isOpen) {
      setActiveGamePack(undefined);
    }
    setIsCreateGamePackDialogOpen(isOpen);
    setIsEditGamePackDialogOpen(isOpen);
  };

  const toggleGamePackEdit = (isOpen: boolean) => {
    if (activeGamePack?.id) {
      setIsEditGamePackDialogOpen(isOpen);
    }
  };

  const toggleGamePackDelete = (isOpen: boolean) => {
    if (!isOpen) {
      setActiveGamePack(undefined);
    }
    setIsDeleteGamePackDialogOpen(isOpen);
  };

  const onDragEnd = async (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const ordered = Array.from(gamePacks);
    const [removed] = ordered.splice(source.index, 1);
    ordered.splice(destination.index, 0, removed);

    // Update local state immediately for better UX
    setGamePacks(ordered);

    // Update order in the database
    try {
      const orders = ordered.map((gamePack, index) => ({
        id: gamePack.id,
        order: index + 1
      }));
      await updateGamePackOrder(orders);
    } catch (error) {
      console.error('Error updating game pack order:', error);
      // Revert to original order on error
      await getGamePacks();
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="overflow-auto h-full">
        <Droppable droppableId="gamepacks">
          {(provided) => (
            <div
              className="w-900 mx-auto content-wrapper"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              <div className="flex justify-between mb-32">
                <h1 className="text-h1 mb-12">Manage Game Packs and Prompts</h1>
              </div>
              <div className="grid grid-cols-12 gap-24">
                <div className="col-span-5">
                  <Panel
                    title={'Game Packs'}
                    headerChildren={
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setIsCreateGamePackDialogOpen(true)}
                      >
                        Create Game Pack
                      </button>
                    }
                  >
                    <div className="max-h-[65vh] overflow-y-auto pr-8 scrollbar-thin">
                      {gamePacks.map((gamePack, index) => (
                        <Draggable key={gamePack.id} draggableId={gamePack.id} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="mb-16"
                            >
                              <GamePackCard
                                key={gamePack.id}
                                gamePack={gamePack}
                                onEdit={() => gamePackEdit(gamePack)}
                                onDelete={() => gamePackDelete(gamePack)}
                                onClick={() => toggleGamePack(gamePack)}
                                isHighlighted={
                                  activeGamePack?.id !== undefined &&
                                  activeGamePack?.id === gamePack.id
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}

                      {gamePacks.length === 0 ? (
                        <h3>
                          <b>No game packs found</b>
                        </h3>
                      ) : (
                        <></>
                      )}
                    </div>
                  </Panel>
                </div>
                <div className="col-span-7">
                  <Panel
                    title={'Prompts'}
                    headerChildren={
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setIsCreatePromptDialogOpen(true)}
                      >
                        Create Prompt
                      </button>
                    }
                  >
                    <div className="max-h-[65vh] overflow-y-auto pr-8 scrollbar-thin">
                      {promptsLoading ? (
                        <div className="mt-[8px] mb-[8px]">
                          <LoadingDots />
                        </div>
                      ) : (
                        <></>
                      )}
                      {getPromptsWithGamePacks().map((prompt) => (
                        <div key={prompt.id}>
                          <PromptCard
                            prompt={prompt}
                            onEdit={() => promptEdit(prompt)}
                            onDelete={() => promptDelete(prompt)}
                            hideGamePacks={false}
                          />
                        </div>
                      ))}
                      {!promptsLoading && getPromptsWithGamePacks().length === 0 ? (
                        <h3>
                          <b>
                            {activeGamePack?.id
                              ? 'No prompts found for this game pack'
                              : 'No prompts found'}
                          </b>
                        </h3>
                      ) : (
                        <></>
                      )}
                    </div>
                  </Panel>
                </div>
                <CreatePromptDialog
                  isOpen={isCreatePromptDialogOpen}
                  setIsOpen={(isOpen) => setIsCreatePromptDialogOpen(isOpen)}
                  gamePackId={activeGamePack?.id}
                  onSubmit={async () => {
                    await refreshGamePackData();
                  }}
                />
                <EditPromptDialog
                  isOpen={isEditPromptDialogOpen}
                  setIsOpen={(isOpen) => toggleEdit(isOpen)}
                  promptId={activePrompt?.id}
                  onSubmit={async () => {
                    await refreshGamePackData();
                  }}
                />
                <DeletePromptDialog
                  isOpen={isDeletePromptDialogOpen}
                  setIsOpen={(isOpen) => toggleDelete(isOpen)}
                  promptId={activePrompt?.id}
                  gamePackPromptId={activeGamePack?.id}
                  onSubmit={async () => {
                    await refreshGamePackData();
                  }}
                />
                <CreateGamePackDialog
                  isOpen={isCreateGamePackDialogOpen}
                  gamePackId={activeGamePack?.id}
                  setIsOpen={(isOpen) => toggleGamePackCreate(isOpen)}
                  onSubmit={async () => await refreshGamePackData()}
                />
                <EditGamePackDialog
                  isOpen={isEditGamePackDialogOpen}
                  gamePackId={activeGamePack?.id}
                  setIsOpen={(isOpen) => toggleGamePackEdit(isOpen)}
                  onSubmit={async () => await refreshGamePackData()}
                />
                <DeleteGamePackDialog
                  isOpen={isDeleteGamePackDialogOpen}
                  setIsOpen={(isOpen) => toggleGamePackDelete(isOpen)}
                  gamePackId={activeGamePack?.id}
                  onSubmit={async () => await refreshGamePackData()}
                />
              </div>
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  );
}
